/**
 * +castux: gentle one-shot resource warnings, data-driven from the class's resources:
 *  - a builder (a pool that starts empty and fills in combat: Maelstrom, Rage, Resolve, Holy
 *    Power) sitting at its cap for CAP_WASTE_MS in combat → "Maelstrom capped" once, re-armed when
 *    it is spent below the cap;
 *  - a healer's spender pool (starts full: Mana) dropping under LOW_POOL_PCT → "Low Mana" once,
 *    re-armed above LOW_POOL_REARM_PCT.
 * "Not enough <resource>" on a failed press is an error (game/castux.ts), deduped by the error line.
 * Pure (unit-tested): `update` returns the warning text to show this frame, else null.
 */
import type { ClassDef, DeepReadonly, ResourceDef, Unit } from '@keystone/sim';

export const CAP_WASTE_MS = 1500;
export const LOW_POOL_PCT = 0.2;
export const LOW_POOL_REARM_PCT = 0.3;

export type ResourceWarnKind = 'builder' | 'pool' | 'none';

/** How a class resource is watched: builders (start empty) warn when capped; healers' full pools warn when low. */
export function resourceWarnKind(r: Pick<ResourceDef, 'start' | 'max'>, role: string | undefined): ResourceWarnKind {
  if (r.start <= 0) return 'builder';
  if (r.start >= r.max && role === 'healer') return 'pool';
  return 'none';
}

interface Watch {
  id: string;
  name: string;
  kind: ResourceWarnKind;
  cappedSince: number;
  armed: boolean;
}

export class ResourceWarnings {
  private classId: string | null = null;
  private watches: Watch[] = [];

  /** `now`: sim time (ms). Returns a warning to show now, else null (one per call at most). */
  update(u: DeepReadonly<Unit> | undefined, cls: Pick<ClassDef, 'id' | 'role' | 'resources'> | undefined, now: number): string | null {
    if (!u || !cls) return null;
    if (cls.id !== this.classId) {
      this.classId = cls.id;
      this.watches = cls.resources
        .map((r) => ({ id: r.id, name: r.name, kind: resourceWarnKind(r, cls.role), cappedSince: -1, armed: true }))
        .filter((w) => w.kind !== 'none');
    }
    let out: string | null = null;
    for (const w of this.watches) {
      const pool = u.resources[w.id];
      if (!pool || pool.max <= 0) continue;
      if (w.kind === 'builder') {
        const capped = pool.cur >= pool.max;
        if (!capped) {
          w.cappedSince = -1;
          w.armed = true;
          continue;
        }
        if (!u.alive || !u.inCombat) {
          w.cappedSince = -1;
          continue;
        }
        if (w.cappedSince < 0) w.cappedSince = now;
        if (w.armed && now - w.cappedSince >= CAP_WASTE_MS && !out) {
          w.armed = false;
          out = `${w.name} capped`;
        }
      } else {
        const frac = pool.cur / pool.max;
        if (frac >= LOW_POOL_REARM_PCT) w.armed = true;
        else if (w.armed && frac < LOW_POOL_PCT && u.alive && u.inCombat && !out) {
          w.armed = false;
          out = `Low ${w.name}`;
        }
      }
    }
    return out;
  }
}
